import { CuriosityAutocomplete, CuriosityNamespace, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorInput,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PurchaseRequisitionFilterField } from '@/generated/gql/graphql';
import { PurchaseRequisitionQueryFieldGroup } from '~/common/types';
import {
  listOperators,
  operators,
  queryFieldFactory,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';

const curiosityNsMap: { [key in PurchaseRequisitionFilterField]?: CuriosityNamespace } = {
  [PurchaseRequisitionFilterField.primaryCommodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [PurchaseRequisitionFilterField.commodityGroupId]: CuriosityNamespaceType.COMMODITY_GROUP,
  [PurchaseRequisitionFilterField.sourceSystemId]: CuriosityNamespaceType.SOURCE_SYSTEM,
};

export const usePurchaseRequisitionDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation();

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'Base.purchaseRequisition' });
    return {
      fields: [
        // ---------- PURCHASE REQUISITION ----------
        createField({
          name: PurchaseRequisitionFilterField.id,
          grp: PurchaseRequisitionQueryFieldGroup.purchaseRequisition,
          operators: operators.text,
        }),
        createField({
          name: PurchaseRequisitionFilterField.externalId,
          grp: PurchaseRequisitionQueryFieldGroup.purchaseRequisition,
          operators: operators.text,
        }),
        createField({
          name: PurchaseRequisitionFilterField.description,
          grp: PurchaseRequisitionQueryFieldGroup.purchaseRequisition,
          operators: operators.text,
        }),
        createField({
          name: PurchaseRequisitionFilterField.totalValue,
          grp: PurchaseRequisitionQueryFieldGroup.purchaseRequisition,
          operators: operators.num,
        }),
        createField({
          name: PurchaseRequisitionFilterField.lineItemsCount,
          grp: PurchaseRequisitionQueryFieldGroup.purchaseRequisition,
          operators: operators.num,
        }),
        // ---------- SUPPLIER ----------
        createField({
          name: PurchaseRequisitionFilterField.fixedSuppliersCount,
          grp: PurchaseRequisitionQueryFieldGroup.supplier,
          operators: operators.num,
        }),
        createField({
          name: PurchaseRequisitionFilterField.preferredSuppliersCount,
          grp: PurchaseRequisitionQueryFieldGroup.supplier,
          operators: operators.num,
        }),
        // ---------- COMMODITY GROUP ----------
        createField({
          name: PurchaseRequisitionFilterField.primaryCommodityGroupId,
          grp: PurchaseRequisitionQueryFieldGroup.commodityGroup,
          operators: operators.textAuto,
        }),
        createField({
          name: PurchaseRequisitionFilterField.commodityGroupId,
          grp: PurchaseRequisitionQueryFieldGroup.commodityGroup,
          operators: operators.textAuto,
        }),
      ],
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          case PurchaseRequisitionFilterField.primaryCommodityGroupId:
          case PurchaseRequisitionFilterField.sourceSystemId:
          case PurchaseRequisitionFilterField.commodityGroupId: {
            const ns = curiosityNsMap[field];
            if (ns) {
              return <CuriosityAutocomplete ns={ns} multiple={listOperators[operator]} simplifyValue {...otherProps} />;
            }
            return null;
          }
          case PurchaseRequisitionFilterField.id:
          case PurchaseRequisitionFilterField.externalId:
          case PurchaseRequisitionFilterField.description:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple={true} freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;

          //
          case PurchaseRequisitionFilterField.preferredSuppliersCount:
          case PurchaseRequisitionFilterField.fixedSuppliersCount:
          case PurchaseRequisitionFilterField.lineItemsCount:
          case PurchaseRequisitionFilterField.totalValue:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
